import type { ReactNode } from 'react';
import { Badge, Button, Card, CardHeader, CardTitle } from '@paige/ui';
import type { AccountCompany, StatementSummary } from './pb-types';
import {
  channelWord,
  day,
  dollars,
  intervalWord,
  monthWord,
  planWord,
  statementLook,
} from './pb-words';
import { ListState, ScrollTable, Th } from './pb-parts';
import './partner-billing.css';

/** A server-side page: where it starts, whether there is a next, and the moves. */
export interface Paging {
  start: number;
  shown: number;
  total?: number;
  hasNext: boolean;
  busy?: boolean;
  onPrev: (() => void) | null;
  onNext: () => void;
}

export function Pager({ p, what }: { p: Paging; what: string }): ReactNode {
  if (!p.onPrev && !p.hasNext) return null;
  const end = p.start + p.shown;
  return (
    <nav className="pb-pager" aria-label={`${what} pages`}>
      <span>
        {p.shown === 0 ? 'None' : `${p.start + 1}–${end}`}
        {p.total !== undefined ? ` of ${p.total.toLocaleString('en-US')}` : ''}
      </span>
      <span className="ad-row">
        <Button size="sm" variant="ghost" disabled={!p.onPrev} onClick={() => p.onPrev?.()}>
          Previous
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!p.hasNext}
          loading={p.busy === true}
          onClick={p.onNext}
        >
          Next
        </Button>
      </span>
    </nav>
  );
}

/** What waits on a company: a channel move, a stop, a pause. */
function flagsOf(c: AccountCompany): string[] {
  return [
    c.suspended ? 'Paused' : null,
    c.endsOn ? `Stops ${day(c.endsOn)}` : null,
    c.channelMovesOn ? `Moves ${day(c.channelMovesOn)}` : null,
  ].filter((x): x is string => x !== null);
}

/** The companies the account pays for, a page at a time from the server. */
export function CompaniesCard(p: {
  companies: AccountCompany[] | undefined;
  paging: Paging;
  renderCompany: (tenantId: string, name: string) => ReactNode;
}): ReactNode {
  const head = (
    <>
      <Th>Company</Th>
      <Th>Plan</Th>
      <Th>Invoice</Th>
      <Th>Renews</Th>
    </>
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Companies it pays for</CardTitle>
      </CardHeader>
      <ListState rows={p.companies} none="It pays for no company now." />
      {p.companies?.length ? (
        <ScrollTable head={head} label="Companies it pays for">
          {p.companies.map((c) => (
            <CompanyRow key={c.tenantId} c={c} render={p.renderCompany} />
          ))}
        </ScrollTable>
      ) : null}
      <Pager p={p.paging} what="Companies" />
    </Card>
  );
}

function CompanyRow(p: {
  c: AccountCompany;
  render: (tenantId: string, name: string) => ReactNode;
}) {
  const c = p.c;
  return (
    <tr>
      <td>
        {p.render(c.tenantId, c.name ?? 'A deleted company')}
        <span className="pb-table__sub">{[c.partnerCode, ...flagsOf(c)].join(' · ')}</span>
      </td>
      <td>
        {planWord(c.package)}
        <span className="pb-table__sub">{intervalWord(c.interval)}</span>
      </td>
      <td>{channelWord[c.invoiceChannel]}</td>
      <td>{day(c.renewsOn)}</td>
    </tr>
  );
}

const STATEMENT_HEAD = (
  <>
    <Th>Month</Th>
    <Th>Statement</Th>
    <Th>State</Th>
    <Th>Due</Th>
    <Th num>Total</Th>
  </>
);

/** The statements, newest first: the open month as it accrues, then issued, paid and void ones. */
export function StatementsCard(p: {
  statements: StatementSummary[] | undefined;
  chosen: string | null;
  today: string;
  paging: Paging;
  onChoose: (id: string) => void;
}): ReactNode {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Statements</CardTitle>
      </CardHeader>
      <ListState
        rows={p.statements}
        none="No statement yet: the first opens with the first line."
      />
      {p.statements?.length ? (
        <ScrollTable head={STATEMENT_HEAD} label="Statements">
          {p.statements.map((s) => (
            <StatementRow
              key={s.id}
              s={s}
              chosen={s.id === p.chosen}
              today={p.today}
              onChoose={p.onChoose}
            />
          ))}
        </ScrollTable>
      ) : null}
      <Pager p={p.paging} what="Statements" />
    </Card>
  );
}

function StatementRow(p: {
  s: StatementSummary;
  chosen: boolean;
  today: string;
  onChoose: (id: string) => void;
}) {
  const look = statementLook(p.s, p.today);
  const label = p.s.number ?? `${monthWord(p.s.month)}, open`;
  return (
    <tr className={p.chosen ? 'is-chosen' : undefined}>
      <td>{monthWord(p.s.month)}</td>
      <td>
        <button
          type="button"
          className="pb-link"
          aria-pressed={p.chosen}
          onClick={() => p.onChoose(p.s.id)}
        >
          {p.s.number ?? 'Open statement'}
          <span className="pui-visually-hidden"> ({label}): show its lines</span>
        </button>
      </td>
      <td>
        <Badge tone={look.tone}>{look.word}</Badge>
        {p.s.paidOn ? <span className="pb-table__sub">paid {day(p.s.paidOn)}</span> : null}
      </td>
      <td>{p.s.state === 'issued' ? day(p.s.dueOn) : '—'}</td>
      <td className="is-num">
        {dollars(p.s.total)}
        {p.s.totalFinal ? null : <span className="pb-table__sub">so far</span>}
      </td>
    </tr>
  );
}
